import type {Schemas} from 'api/dto.generated';
import {ebsApi} from 'store/ebsApi';
import type {Collection, CollectionWithoutLinks} from './types';
import {
  mapGetCollectionDto,
  mapSeriesDto,
  mapToUpsertCollectionDto,
} from './mappers';

const seriesApi = ebsApi.injectEndpoints({
  endpoints: build => ({
    getSeries: build.query<
      Collection,
      Pick<Collection, 'id'>,
      Schemas.SeriesWithRelationsDto
    >({
      keepUnusedDataFor: 60,
      query: ({id}) => ({
        url: `series/${id}`,
      }),
      transformResponse: mapGetCollectionDto,
      providesTags: (_, __, {id}) => [{type: 'Series', id}],
    }),

    getSeriesByIds: build.query<
      Array<CollectionWithoutLinks>,
      Array<Collection['id']>,
      Array<Schemas.SeriesDto>
    >({
      keepUnusedDataFor: 60,
      query: ids => ({
        url: `series/ids`,
        method: 'POST',
        body: ids,
      }),
      transformResponse: r => r.map(mapSeriesDto),
    }),

    mergeSeries: build.mutation<
      void,
      {target: Collection; source: Pick<Collection, 'id'>}
    >({
      query: ({target, source}) => ({
        url: `series/${target.id}/merge/${source.id}`,
        method: 'PUT',
        body: mapToUpsertCollectionDto(target),
      }),
      invalidatesTags: (_, __, {target, source}) => [
        {type: 'Series', id: target.id},
        {type: 'Series', id: source.id},
      ],
    }),

    createSeries: build.mutation<
      Collection,
      Omit<Collection, 'id'>,
      Schemas.SeriesWithRelationsDto
    >({
      query: collection => ({
        url: `series/`,
        method: 'POST',
        body: mapToUpsertCollectionDto(collection),
      }),
      invalidatesTags: [{type: 'Series'}],
      transformResponse: mapGetCollectionDto,
    }),

    updateSeries: build.mutation<
      Collection,
      Collection,
      Schemas.SeriesWithRelationsDto
    >({
      query: collection => ({
        url: `series/${collection.id}`,
        method: 'PUT',
        body: mapToUpsertCollectionDto(collection),
      }),
      invalidatesTags: r => [{type: 'Series', id: r!.id}],
      transformResponse: mapGetCollectionDto,
    }),
  }),
});

export const {
  useGetSeriesQuery,
  useLazyGetSeriesQuery,
  useMergeSeriesMutation,
  useCreateSeriesMutation,
  useUpdateSeriesMutation,
  useLazyGetSeriesByIdsQuery,
} = seriesApi;
